import { useEffect, useRef, useState, createElement } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { fetchGramsciProject } from '../api';
import { isPetition, PETITION } from '../projectType';
import { htmlToParagraphs } from '../richText';
import { FIELD_RULES, isValid } from '../validation';
import TermsModal, { resolveConsentTerms } from '../components/TermsModal';

// The petition landing page, at /petitions/:slug.
//
// Replaces the Wix pages of the same shape (standing-together.org/petitions/
// :slug), so the URL keeps the plural — links already shared have to keep
// working. Behaviour follows those pages:
//
//   wide    two columns — petition text on the right, form on the left,
//           vertically centred and scrolling internally only if it is taller
//           than the viewport. Page scroll stays the browser's own.
//   narrow  one column, form LAST, with a sticky bar riding the bottom of the
//           viewport that scrolls down to it.
//
// The sticky bar is the part worth getting right: on a phone the page is a long
// scroll with the form below nearly all of it, so without a persistent call to
// action a reader never learns the form exists. It is `position: sticky` and
// sits immediately before the form in the DOM, so it rides the viewport while
// there is page left and then comes to rest above the form instead of covering
// it forever.
//
// Signing is not wired — there is no endpoint on either side — so submitting
// says so rather than pretending. The enable/disable rule is real, though.

// ---------------------------------------------------------------------------
// HOW LONG THE JOIN BUTTON TAKES TO SCROLL DOWN TO THE FORM, in milliseconds.
// Bigger = slower. This is the knob to turn.
//
// It has to be animated by hand: CSS `scroll-behavior: smooth` and
// scrollTo({ behavior: 'smooth' }) both let the browser choose the duration and
// give no way to ask for a different one. The live Wix page animates it in JS
// for the same reason — its scroll-behavior is `auto` everywhere.
// ---------------------------------------------------------------------------
const SCROLL_DURATION_MS = 1400;

// Quick off the mark, settling gently, so the form arrives rather than
// stopping dead.
const easeOutCubic = (t) => 1 - (1 - t) ** 3;

function scrollToSign(e) {
  const target = document.getElementById('sign');
  // Nothing to scroll to — let the href fall back to its default behaviour.
  if (!target) return;
  e.preventDefault();

  const startY = window.scrollY;
  const endY = startY + target.getBoundingClientRect().top;

  if (SCROLL_DURATION_MS <= 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo(0, endY);
    return;
  }

  // A scroll this long is worth being able to interrupt: someone who starts
  // scrolling themselves shouldn't have to fight the animation for a second.
  let stopped = false;
  const stop = () => { stopped = true; };
  window.addEventListener('wheel', stop, { once: true, passive: true });
  window.addEventListener('touchstart', stop, { once: true, passive: true });
  const done = () => {
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
  };

  const startedAt = performance.now();
  const step = (now) => {
    if (stopped) return done();
    const t = Math.min(1, (now - startedAt) / SCROLL_DURATION_MS);
    window.scrollTo(0, startY + (endY - startY) * easeOutCubic(t));
    if (t < 1) requestAnimationFrame(step);
    else done();
  };
  requestAnimationFrame(step);
}

const DEFAULT_JOIN_TEXT_1 = 'הצטרפו לדרישה';
const DEFAULT_JOIN_TEXT_2 = 'انضموا للمطلب';

// Bilingual labels, both languages in one string, the way the live form writes
// them. Keyed by the field types Gramsci already uses for landing pages, which
// is the vocabulary a petition's `fields` list will speak.
const FIELD_LABELS = {
  first_name: 'שם פרטי الاسم الشخصي',
  last_name: 'שם משפחה اسم العائلة',
  residency: 'יישוב مكان الاقامة',
  phone: 'טלפון رقم الهاتف',
  email: 'דואר אלקטרוני البريد الالكتروني',
  workplace: 'מקום העבודה مكان العمل',
  university: 'מוסד לימודים مؤسسة أكاديميّة',
  program: 'חוג לימודים برنامج الدراسة',
};

// Format rules, reusing the shared ones so a phone number accepted on the
// donate page is accepted here too (see validation.js). Types absent from this
// map — workplace, university, program — are free text: required-ness is all
// that is checked.
const RULE_FOR_TYPE = {
  first_name: FIELD_RULES.firstName,
  last_name: FIELD_RULES.lastName,
  residency: FIELD_RULES.city,
  phone: FIELD_RULES.phone,
  email: FIELD_RULES.email,
};

const INPUT_TYPE = { email: 'email', phone: 'tel' };

// Which language a block is written in. The record carries header1/text1 rather
// than _he/_ar — that is what lets an editor put Arabic first — so it no longer
// says, and the script has to. Hebrew and Arabic sit in distinct Unicode
// ranges, so counting letters is enough. Both are RTL, so only `lang` changes,
// and it matters: it drives font selection and how a screen reader pronounces
// the text.
function scriptLang(html) {
  const text = String(html || '').replace(/<[^>]*>/g, '');
  const he = (text.match(/[֐-׿]/g) || []).length;
  const ar = (text.match(/[؀-ۿ]/g) || []).length;
  if (!he && !ar) return undefined;
  return ar > he ? 'ar' : 'he';
}

// Translate the record into the shape this page renders — the petition
// counterpart of normalizeCampaign. Returns null for a missing or unpublished
// petition; the page then redirects home.
export function normalizePetition(live) {
  if (!live || live.is_active === false) return null;

  const count = Number(live.signature_count);

  // Only field types we have a label for; an unknown type from a future Gramsci
  // build is skipped rather than rendered as a blank row.
  const fields = (Array.isArray(live.fields) ? live.fields : [])
    .filter((f) => f && FIELD_LABELS[f.type])
    .map((f) => ({
      type: f.type,
      label: FIELD_LABELS[f.type],
      required: !!f.required,
      rule: RULE_FOR_TYPE[f.type] || null,
    }));

  return {
    headers: [live.header1, live.header2]
      .filter((h) => typeof h === 'string' && h.trim())
      .map((h) => ({ text: h, lang: scriptLang(h) })),
    texts: [live.text1, live.text2]
      .map((t) => ({ blocks: htmlToParagraphs(t), lang: scriptLang(t) }))
      .filter((t) => t.blocks),
    // Gramsci stores a bare S3 filename we can't resolve client-side, so only
    // use it when it's an absolute URL or a root-relative path we serve.
    image: /^(https?:\/\/|\/)/.test(live.image || '') ? live.image : undefined,
    signed: Number.isFinite(count) ? count : null,
    joinText1: (live.join_text_1 || '').trim() || DEFAULT_JOIN_TEXT_1,
    joinText2: (live.join_text_2 || '').trim() || DEFAULT_JOIN_TEXT_2,
    fields,
    // Consent statement from the server (null = fallback message).
    consentTerms: live.consent_terms || null,
    // Mock-only flag; never sent by Gramsci.
    mock: live.mock === true,
  };
}

// Description blocks are { tag, html } pairs — same contract as the campaign
// page's, so an <h3> authored in Gramsci renders (and styles) as a heading.
function Blocks({ blocks }) {
  return blocks.map(({ tag, html, only }, i) =>
    createElement(tag, {
      key: i,
      className: only === 'strong' ? 'pet-statement' : undefined,
      dangerouslySetInnerHTML: { __html: html },
    })
  );
}

const nf = new Intl.NumberFormat('he-IL');

function consentSourceLabel(terms) {
  const { source } = resolveConsentTerms(terms);
  return `consent terms source: ${source === 'gramsci' ? 'Gramsci' : 'missing'}`;
}

export default function PetitionPage() {
  const { slug } = useParams();
  const [petition, setPetition] = useState(null);
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);

  const [values, setValues] = useState({});
  // A field is only ever marked wrong after the visitor has left it, so nothing
  // turns red while they are still typing the first letter.
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // PETITION tells the backend to look this slug up among petitions rather
    // than campaigns — reaching this page IS the statement that it is one.
    fetchGramsciProject(slug, PETITION).then((live) => {
      if (cancelled) return;
      setRaw(live);
      setPetition(normalizePetition(live));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [slug]);

  // Keep the sticky form's sign button reachable on short windows.
  const asideRef = useRef(null);
  useEffect(() => {
    const aside = asideRef.current;
    if (!aside) return undefined;
    const place = () => {
      const overflow = Math.min(0, window.innerHeight - aside.offsetHeight);
      aside.style.setProperty('--pet-aside-top', `${overflow}px`);
    };
    place();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    if (ro) ro.observe(aside);
    window.addEventListener('resize', place);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [petition]);

  // Still fetching: render nothing rather than flashing a not-found state.
  if (loading) return null;

  // A campaign reached at a petition URL. The URL and the record disagree, and
  // the record wins — it is the one that knows what it is. CampaignPage does
  // the mirror image of this, so a link of either shape lands correctly.
  // On the petition host /:slug IS the petition URL, so there is no campaign
  // URL to forward to - home instead, or this would bounce to itself forever.
  if (raw && !isPetition(raw)) {
    const onPetitionHost = window.location.hostname.startsWith('petition.') || window.location.hostname.startsWith('www.petition.');
    return <Navigate to={onPetitionHost ? '/' : `/${slug}`} replace />;
  }

  // Unknown or unpublished slug (or Gramsci unreachable) — home, as the
  // campaign page does.
  if (!petition) return <Navigate to="/" replace />;

  // Wrong only when there is something to be wrong about:
  //   required and left empty  -> wrong, once touched
  //   optional and left empty  -> never wrong, that is what optional means
  //   filled but malformed     -> wrong either way; a bad email is bad even
  //                               when nobody asked for it
  // A signature with no way to reach the signer is useless, so when the form
  // asks for phone and/or email at least one of the two must be filled - no
  // matter how their required flags are set in Gramsci. Editor toggles can't
  // be trusted with this; the form enforces it itself. Filled-but-malformed
  // is a separate failure, handled by each field's own rule below.
  const contactTypes = petition.fields.map((f) => f.type).filter((t) => t === 'phone' || t === 'email');
  const contactMissing = contactTypes.length > 0 && contactTypes.every((t) => !(values[t] || '').trim());
  // Flags both contact inputs once the signer has been in either of them -
  // otherwise the disabled button has no visible explanation.
  const contactWrong = contactMissing && contactTypes.some((t) => touched[t]);

  const fieldError = (f) => {
    if ((f.type === 'phone' || f.type === 'email') && contactWrong) return true;
    const v = (values[f.type] || '').trim();
    if (!touched[f.type]) return false;
    if (!v) return f.required;
    return f.rule ? !isValid(f.rule, v) : false;
  };

  const canSubmit = petition.fields.every((f) => {
    const v = (values[f.type] || '').trim();
    if (!v) return !f.required;
    return f.rule ? isValid(f.rule, v) : true;
  }) && !contactMissing;

  const setField = (type, v) => setValues((prev) => ({ ...prev, [type]: v }));
  const blur = (type) => setTouched((prev) => ({ ...prev, [type]: true }));

  const form = (
    <div className="pet-form" id="sign">
      <h2 className="pet-form-title">
        <span>{petition.joinText1}</span>
        <span lang="ar">{petition.joinText2}</span>
      </h2>

      {petition.signed != null && (
        <p className="pet-count">
          <strong>{nf.format(petition.signed)}</strong> כבר חתמו وقعوا حتى الآن
        </p>
      )}

      {petition.fields.map((f) => {
        const wrong = fieldError(f);
        return (
          <label key={f.type} className="pet-field">
            <span className="pet-label">
              {f.label}
              {f.required && <b aria-hidden="true"> *</b>}
            </span>
            <input
              type={INPUT_TYPE[f.type] || 'text'}
              name={f.type}
              value={values[f.type] || ''}
              required={f.required}
              aria-invalid={wrong || undefined}
              className={wrong ? 'pet-input--error' : undefined}
              onChange={(e) => setField(f.type, e.target.value)}
              onBlur={() => blur(f.type)}
            />
          </label>
        );
      })}

      <button
        type="button"
        className="pet-submit"
        disabled={!canSubmit}
        onClick={() => setAttempted(true)}
      >
        חתימה توقيع
      </button>

      {attempted && (
        <p className="pet-pending" role="status">
          טופס החתימה טרם חובר — אין עדיין נקודת קצה לחתימות ב-Gramsci
        </p>
      )}

      <p className="pet-terms">
        <span>
          בשליחה אני מסכימ/ה{' '}
          <button type="button" className="pet-terms-link" onClick={() => setTermsOpen(true)}>לתנאי השימוש</button>
        </span>
        <span lang="ar">
          بالإرسال، أنا أوافق على{' '}
          <button type="button" className="pet-terms-link" onClick={() => setTermsOpen(true)}>شروط الاستخدام</button>
        </span>
      </p>

      {/* Narrow only. The logo belongs to the petition text on a wide screen
          and to the foot of the signing section on a narrow one — two different
          parents, so CSS can't move a single copy between them. Rendering both
          and hiding one with display:none keeps it out of the accessibility
          tree too, so it is never announced twice. */}
      <p className="pet-logo pet-logo--signing">
        <img src="/images/logo-purple.png" alt="עומדים ביחד نقف معًا" />
      </p>
    </div>
  );

  return (
    <div className="petition" dir="rtl" lang="he">
      {/* One dialog for both terms links (Hebrew and Arabic). */}
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} terms={petition.consentTerms} />

      {/* Developer bar, mock data only. <bdi> keeps the English in order inside the RTL bar. */}
      {petition.mock && (
        <p className="pet-marker">
          עמוד עצומה · PETITION PAGE · mock data · <bdi>{consentSourceLabel(petition.consentTerms)}</bdi>
        </p>
      )}

      <div className="pet-wrap">
        <div className="pet-cols">
          {/* Content first in the DOM, so a narrow screen reads it before the
              form. The wide layout puts the form back alongside with grid
              placement rather than by reordering markup, which would take it
              out of tab order. */}
          <main className="pet-content">
            <header className="pet-head">
              {petition.headers.map((h, i) => (
                <h1 key={i} className="pet-title" lang={h.lang}>{h.text}</h1>
              ))}
            </header>

            {petition.image && <img className="pet-image" src={petition.image} alt="" />}

            {petition.texts.map((t, i) => (
              <div key={i} className="pet-body" lang={t.lang}>
                <Blocks blocks={t.blocks} />
              </div>
            ))}

            {/* Wide only — see the note on its twin at the foot of the form. */}
            <p className="pet-logo pet-logo--petition">
              <img src="/images/logo-purple.png" alt="עומדים ביחד نقف معًا" />
            </p>
          </main>

          {/* Narrow only (hidden by CSS on wide). This is not a floating button
              over the page — it is the TOP EDGE of the signing section, sitting
              in flow directly above the form. `position: sticky; bottom: 0`
              then gives the behaviour: pinned to the bottom of the viewport
              while the petition is still being read, rising as the signing
              section arrives, and scrolling away once it is above the fold.
              It has to be a SIBLING of the form rather than wrapped with it in
              a section — a sticky element is confined to its parent's box, so
              a wrapper starting below the fold would hide it at page top. */}
          {/* Two elements, not one: the band is what sticks and what supplies
              the white margin around the button — the petition must not show
              through beside it — and the button inside sits at the same width
              as the form's חתימה, since they are the same control at two
              points in the page. */}
          <div className="pet-sticky">
            <a className="pet-join" href="#sign" onClick={scrollToSign}>
              {petition.joinText1} {petition.joinText2}
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </a>
          </div>

          <aside className="pet-aside" ref={asideRef}>{form}</aside>
        </div>
      </div>
    </div>
  );
}
